import React, { useState, useEffect } from "react";
import CustomIconButton from "../Buttons/CustomIconButton";
import { HiOutlinePencil } from "react-icons/hi";
import BusinessHoursModal from "../business_hours/BusinessHoursModal";
import {
    BusinessHours,
    BusinessHoursEditorProps,
} from "../../types/business_hours.t";

const dayMapping: { [key: number]: string } = {
    1: "Lundi",
    2: "Mardi",
    3: "Mercredi",
    4: "Jeudi",
    5: "Vendredi",
    6: "Samedi",
    0: "Dimanche",
};

const defaultBusinessHours: BusinessHours = {
    1: {
        open_times: [{ open_time: "--", close_time: "--" }],
        is_closed: false,
        is_24_hours: false,
    },
    2: {
        open_times: [{ open_time: "--", close_time: "--" }],
        is_closed: false,
        is_24_hours: false,
    },
    3: {
        open_times: [{ open_time: "--", close_time: "--" }],
        is_closed: false,
        is_24_hours: false,
    },
    4: {
        open_times: [{ open_time: "--", close_time: "--" }],
        is_closed: false,
        is_24_hours: false,
    },
    5: {
        open_times: [{ open_time: "--", close_time: "--" }],
        is_closed: false,
        is_24_hours: false,
    },
    6: {
        open_times: [{ open_time: "--", close_time: "--" }],
        is_closed: false,
        is_24_hours: false,
    },
    0: {
        open_times: [{ open_time: "--", close_time: "--" }],
        is_closed: false,
        is_24_hours: false,
    },
};

export default function BusinessHoursEditor({
    businessHours,
    isEditing,
    onSave,
}: BusinessHoursEditorProps) {
    const [hours, setHours] = useState<BusinessHours>({});
    const [modalVisible, setModalVisible] = useState(false);

    // Function to update the hours when the modal changes them
    const handleUpdatedHours = (updatedHours: BusinessHours) => {
        setHours(updatedHours);
        onSave(updatedHours); // Send the update to ClientBottom
    };

    useEffect(() => {
        // Merge businessHours with defaultBusinessHours, replacing empty values
        const updatedHours: BusinessHours = Object.keys(
            defaultBusinessHours
        ).reduce((acc, day) => {
            const dayNumber = Number(day);
            acc[dayNumber] =
                businessHours?.[dayNumber] &&
                Object.keys(businessHours[dayNumber]).length
                    ? businessHours[dayNumber]
                    : defaultBusinessHours[dayNumber];
            return acc;
        }, {} as BusinessHours);

        setHours(updatedHours);
    }, [businessHours]);

    const toggleModal = () => setModalVisible((prev) => !prev);

    return (
        <div className="max-w-md mx-auto bg-white mt-2 w-full justify-start">
            <ul>
                {Object.entries(hours)
                    .sort(([a], [b]) => {
                        const dayA = Number(a);
                        const dayB = Number(b);

                        // Sort days so Monday (1) to Saturday (6) come first, followed by Sunday (0)
                        if (dayA === 0) return 1; // Move Sunday to the end
                        if (dayB === 0) return -1; // Move Sunday to the end
                        return dayA - dayB; // Sort the rest in ascending order
                    })
                    .map(([day, { open_times, is_closed, is_24_hours }]) => (
                        <li key={day} className="flex flex-col">
                            <div className="flex flex-row py-1 w-full">
                                <div className="w-2/4 text-black text-sm">
                                    {dayMapping[Number(day)]}
                                </div>
                                <div className="w-2/4">
                                    {is_closed ? (
                                        <span className="text-sm text-black">
                                            Fermé
                                        </span>
                                    ) : is_24_hours ? (
                                        <span className="text-sm text-black">
                                            Ouvert 24h
                                        </span>
                                    ) : open_times.length === 0 ? (
                                        <div className="text-sm text-gray-500">
                                            -- - --
                                        </div>
                                    ) : (
                                        open_times.map((period, index) => (
                                            <div
                                                key={index}
                                                className="flex flex-row items-center"
                                            >
                                                <div className="my-1 text-sm text-black">
                                                    {`${
                                                        period.open_time
                                                            .length === 8
                                                            ? period.open_time.slice(
                                                                  0,
                                                                  -3
                                                              )
                                                            : period.open_time
                                                    } - ${
                                                        period.close_time
                                                            .length === 8
                                                            ? period.close_time.slice(
                                                                  0,
                                                                  -3
                                                              )
                                                            : period.close_time
                                                    }`}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="w-1/5">
                                    {isEditing && (
                                        <CustomIconButton
                                            onClick={toggleModal}
                                            icon={<HiOutlinePencil />}
                                            sx={{
                                                fontSize: "0.75rem",
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
            </ul>

            {modalVisible && (
                <BusinessHoursModal
                    toggleModal={toggleModal}
                    businessHours={hours}
                    onUpdate={handleUpdatedHours}
                />
            )}
        </div>
    );
}
